import React, { useState } from 'react';
import './ReviewForm.css';

const ReviewForm = ({
  onSubmit, onCancel, initialLocation, isLoading,
}) => {
  const [formData, setFormData] = useState({
    // Location info
    address: '',
    latitude: initialLocation?.lat || '',
    longitude: initialLocation?.lng || '',

    // Required field
    recommend: '',

    // NEW: Accommodation type
    accommodationType: '', // private, shared, roommate

    // House/building questions
    haunted: '',
    neighborhoodSafety: '',
    parkingQuality: '',
    trafficLevel: '',
    ambulancePoliceSounds: '',
    policePresence: '',
    landlordResponsiveness: '',
    landlordEthics: '',
    landlordEthicsEmoji: '',
    landlordProfessionalism: '',
    landlordCarelessness: '',
    landlordCarelessnessDetails: '',
    landlordMaintenance: '', // NEW: maintenance quality with emojis
    noiseLevel: '',
    neighborsBehavior: '',
    neighborsViolent: '',
    safetyFeel: '',
    accidentsOnRoad: '',
    accidentsDetails: '',
    homelessPeople: '',
    homelessDetails: '',
    kitchenQuality: '',
    airCirculation: '',
    viewQuality: '',
    hasYard: '',
    yardDetails: '',
    swampAreaStreet: '',
    swampAreaYard: '',
    areaCleanliness: '',
    electricityBill: '',
    electricityIncluded: '',
    waterBill: '',
    waterIncluded: '',
    gasBill: '',
    gasIncluded: '',
    internetBill: '',
    internetIncluded: '',
    floorLevel: '',
    apartmentNumber: '',
    floorSqueaky: '', // NEW: floor squeakiness with emojis
    externalOdors: '',
    internalOdors: '',
    basementSmells: '',
    sharedSmells: '',
    hasElevator: '',
    elevatorQuality: '',
    leakyRoofsThefts: '',
    leakyDetails: '',
    maintenanceQuality: '',
    numberOfRooms: '',
    acInAllRooms: '',
    acNotes: '',
    extractorsInBathrooms: '',
    extractorNotes: '',
    // NEW: Laundry facilities
    washerLocation: '', // in-unit, shared, none
    dryerLocation: '', // in-unit, shared, none
    laundryQuality: '', // NEW: quality with emojis
    otherProblems: '',
    petsAllowed: '',
    petsExtraCharge: '',
    garageStorage: '',
    storageSize: '',
    totalArea: '',
    recommendNotes: '',
    otherDetails: '',
  });

  const handleInputChange = (e) => {
    const {
      name, value, type, checked,
    } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validation - only recommend is required
    if (!formData.recommend) {
      alert('Please indicate whether you recommend this rental.');
      return;
    }

    // Convert numeric fields
    const processedData = {
      ...formData,
      latitude: parseFloat(formData.latitude),
      longitude: parseFloat(formData.longitude),
      neighborhoodSafety: formData.neighborhoodSafety ? parseInt(formData.neighborhoodSafety) : null,
      trafficLevel: formData.trafficLevel ? parseInt(formData.trafficLevel) : null,
      ambulancePoliceSounds: formData.ambulancePoliceSounds ? parseInt(formData.ambulancePoliceSounds) : null,
      noiseLevel: formData.noiseLevel ? parseInt(formData.noiseLevel) : null,
      safetyFeel: formData.safetyFeel ? parseInt(formData.safetyFeel) : null,
      accidentsOnRoad: formData.accidentsOnRoad ? parseInt(formData.accidentsOnRoad) : null,
      homelessPeople: formData.homelessPeople ? parseInt(formData.homelessPeople) : null,
      areaCleanliness: formData.areaCleanliness ? parseInt(formData.areaCleanliness) : null,
      numberOfRooms: formData.numberOfRooms ? parseInt(formData.numberOfRooms) : null,
      totalArea: formData.totalArea ? parseFloat(formData.totalArea) : null,
      createdAt: new Date().toISOString(),
      id: Date.now().toString(), // Simple ID for now
    };

    onSubmit(processedData);
  };

  const renderRatingScale = (name, label, explanation = '', min = 1, max = 10) => (
    <div className="form-group">
      <label htmlFor={name}>
        {label}
        {explanation && <div className="scale-explanation">{explanation}</div>}
      </label>
      <select
        id={name}
        name={name}
        value={formData[name]}
        onChange={handleInputChange}
      >
        <option value="">Select rating</option>
        {Array.from({ length: max - min + 1 }, (_, i) => (
          <option key={i + min} value={i + min}>{i + min}</option>
        ))}
      </select>
    </div>
  );

  const renderYesNo = (name, label) => (
    <div className="form-group">
      <label htmlFor={name}>{label}</label>
      <select
        id={name}
        name={name}
        value={formData[name]}
        onChange={handleInputChange}
      >
        <option value="">Select</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    </div>
  );

  const renderTextArea = (name, label, placeholder = '') => (
    <div className="form-group">
      <label htmlFor={name}>{label}</label>
      <textarea
        id={name}
        name={name}
        value={formData[name]}
        onChange={handleInputChange}
        placeholder={placeholder}
        rows="3"
      />
    </div>
  );

  const renderEmojiSelector = (name, label) => (
    <div className="form-group">
      <label htmlFor={name}>{label}</label>
      <div className="emoji-selector">
        {[
          { value: '😊', label: 'Happy' },
          { value: '😐', label: 'Neutral' },
          { value: '😠', label: 'Angry' },
        ].map((emoji) => (
          <button
            key={emoji.value}
            type="button"
            className={`emoji-btn ${formData[name] === emoji.value ? 'selected' : ''}`}
            onClick={() => setFormData((prev) => ({ ...prev, [name]: emoji.value }))}
            title={emoji.label}
          >
            {emoji.value}
          </button>
        ))}
      </div>
    </div>
  );

  const renderSentimentScale = (name) => (
    <div className="sentiment-scale">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => {
        const emojis = ['😊', '🙂', '😐', '😕', '😤', '😠', '😡', '🤬', '😱', '💀'];
        return (
          <button
            key={value}
            type="button"
            className={`sentiment-btn ${formData[name] == value ? 'selected' : ''}`}
            onClick={() => setFormData((prev) => ({ ...prev, [name]: value }))}
            title={`Rate ${value}/10`}
          >
            {value}
            <span className="sentiment-emoji">{emojis[value - 1]}</span>
          </button>
        );
      })}
    </div>
  );

  const renderTextInput = (name, label, type = 'text', placeholder = '') => (
    <div className="form-group">
      <label htmlFor={name}>{label}</label>
      <input
        type={type}
        id={name}
        name={name}
        value={formData[name]}
        onChange={handleInputChange}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div className="review-form-container">
      <div className="review-form-header">
        <h2>Add Rental Review</h2>
        <p>Share your experience with this rental property</p>
      </div>

      <form onSubmit={handleSubmit} className="review-form">
        {/* Location Information */}
        <div className="form-section">
          <h3>Location</h3>
          {renderTextInput('address', 'Address (auto-filled if available)', 'text', 'Street address')}
          {renderTextInput('latitude', 'Latitude', 'number', 'Latitude coordinate')}
          {renderTextInput('longitude', 'Longitude', 'number', 'Longitude coordinate')}
        </div>

        {/* Overall Recommendation - FIRST QUESTION */}
        <div className="form-section">
          <h3>⭐ Overall Recommendation</h3>
          <div className="form-group required">
            <label htmlFor="recommend">Would you recommend this rental? *</label>
            <div className="recommendation-options">
              <button
                type="button"
                className={`recommend-btn ${formData.recommend === 'yes' ? 'selected' : ''}`}
                onClick={() => setFormData((prev) => ({ ...prev, recommend: 'yes' }))}
              >
                ✅ Yes, I would recommend it
              </button>
              <button
                type="button"
                className={`recommend-btn ${formData.recommend === 'no' ? 'selected' : ''}`}
                onClick={() => setFormData((prev) => ({ ...prev, recommend: 'no' }))}
              >
                ❌ No, I would not recommend it
              </button>
            </div>
          </div>
          {renderTextArea('recommendNotes', 'Why or why not?', 'Please share the main reasons for your recommendation...')}
        </div>

        {/* Accommodation Type */}
        <div className="form-section">
          <h3>🏠 Accommodation Type</h3>
          <div className="form-group">
            <label htmlFor="accommodationType">What type of rental is this?</label>
            <select
              id="accommodationType"
              name="accommodationType"
              value={formData.accommodationType}
              onChange={handleInputChange}
            >
              <option value="">Select type</option>
              <option value="private">🏠 Private (entire place to yourself)</option>
              <option value="shared">🏢 Shared (you share with landlord/family)</option>
              <option value="roommate">👥 Roommates (you share with other tenants)</option>
            </select>
          </div>
        </div>

        {/* Safety & Environment */}
        <div className="form-section">
          <h3>🛡️ Safety & Environment</h3>
          <div className="form-group">
            <label>How would you rate the overall safety of this neighborhood?</label>
            {renderSentimentScale('neighborhoodSafety')}
            <div className="scale-description">
              😊 Very safe (24/7 safe) → 😱 Very unsafe (avoid at night)
            </div>
          </div>

          <div className="form-group">
            <label>How would you describe the parking situation?</label>
            {renderTextArea('parkingQuality', '', 'Is there street parking? Garage? How reliable is it?')}
          </div>

          <div className="form-group">
            <label>How busy is the traffic on nearby streets?</label>
            {renderSentimentScale('trafficLevel')}
            <div className="scale-description">
              😊 Very quiet (almost no traffic) → 😤 Extremely busy (constant traffic jams)
            </div>
          </div>

          <div className="form-group">
            <label>How noisy is this location overall?</label>
            {renderTextArea('noiseLevel', '', 'Traffic, neighbors, trains, construction, animals, etc.')}
          </div>

          <div className="form-group">
            <label>How would you rate the cleanliness of the surrounding area?</label>
            {renderSentimentScale('areaCleanliness')}
            <div className="scale-description">
              ✨ Resort quality (immaculate) → 😣 Poor (garbage, litter everywhere)
            </div>
          </div>

          <div className="form-group">
            <label>How would you rate your overall feeling of safety here?</label>
            {renderSentimentScale('safetyFeel')}
            <div className="scale-description">
              😊 Excellent (very comfortable) → 😱 Terrifying (constant fear)
            </div>
          </div>

          {renderYesNo('haunted', '👻 Any supernatural experiences or "haunted" reputation?')}
          {renderTextInput('ambulancePoliceSounds', 'How often do you hear emergency sirens?', 'number', 'Times per day (optional)')}
          {renderTextArea('policePresence', 'Police activity', 'How often do you see police cars or activity?')}
          {renderTextArea('neighborsBehavior', 'Neighbor interactions', 'How are the neighbors? Friendly, noisy, keep to themselves?')}
          {renderYesNo('neighborsViolent', 'Have you witnessed any violent incidents involving neighbors?')}
          {renderTextInput('accidentsOnRoad', 'Traffic accidents on nearby roads', 'number', 'Per year (approximate)')}
          {renderTextArea('accidentsDetails', 'Details about accidents', 'Were they serious? Common causes?')}
          {renderTextInput('homelessPeople', 'Homeless individuals in the area', 'number', 'People you typically see per day')}
          {renderTextArea('homelessDetails', 'Homeless situation details', 'How does this affect the area?')}
          {renderYesNo('swampAreaStreet', 'Is there standing water/flooding on the street after rain?')}
          {renderYesNo('swampAreaYard', 'Is there standing water/flooding in the yard/garden?')}
        </div>

        {/* Property Details */}
        <div className="form-section">
          <h3>🏡 Property Details</h3>
          <div className="form-group">
            <label htmlFor="floorLevel">Which floor is your unit on?</label>
            <select
              id="floorLevel"
              name="floorLevel"
              value={formData.floorLevel}
              onChange={handleInputChange}
            >
              <option value="">Select floor level</option>
              <option value="basement">🏠 Basement</option>
              <option value="ground">🏠 Ground floor</option>
              <option value="middle">🏢 Middle floor (2-10)</option>
              <option value="top">🏙️ Top floor (penthouse/high rise)</option>
            </select>
          </div>

          <div className="form-group">
            <label>How squeaky are the floors?</label>
            {renderSentimentScale('floorSqueaky')}
            <div className="scale-description">
              😊 Solid and quiet → 😤 Extremely squeaky and annoying
            </div>
          </div>

          {renderTextInput('apartmentNumber', 'Unit/Apartment number (if applicable)')}
          {renderTextInput('numberOfRooms', 'Total number of rooms', 'number')}
          {renderTextInput('totalArea', 'Total area in square feet', 'number')}

          <div className="form-group">
            <label>How would you rate the kitchen quality?</label>
            {renderTextArea('kitchenQuality', '', 'Appliances, counter space, storage, cleanliness, etc.')}
          </div>

          <div className="form-group">
            <label>How is the air circulation and ventilation?</label>
            {renderTextArea('airCirculation', '', 'Windows, fans, stuffiness, fresh air flow, etc.')}
          </div>

          <div className="form-group">
            <label>What are the views like from this property?</label>
            {renderTextArea('viewQuality', '', 'City skyline, mountains, parking lot, neighbor windows, etc.')}
          </div>

          {renderYesNo('hasYard', 'Does this property have a yard/garden/outdoor space?')}
          {formData.hasYard === 'yes' && renderTextArea('yardDetails', 'Yard details', 'Size, condition, privacy, usable space?')}

          {renderYesNo('hasElevator', 'Is there an elevator in the building?')}
          {formData.hasElevator === 'yes' && (
            <div className="form-group">
              <label>How would you rate the elevator quality?</label>
              <select
                id="elevatorQuality"
                name="elevatorQuality"
                value={formData.elevatorQuality}
                onChange={handleInputChange}
              >
                <option value="">Select quality</option>
                <option value="excellent">🚀 Excellent (fast, reliable)</option>
                <option value="good">✅ Good (works well)</option>
                <option value="slow">🐌 Slow but functional</option>
                <option value="broken">❌ Often broken/unreliable</option>
              </select>
            </div>
          )}
        </div>

        {/* Laundry Facilities */}
        <div className="form-section">
          <h3>👕 Laundry Facilities</h3>
          <div className="form-group">
            <label>Where is the washing machine located?</label>
            <select
              id="washerLocation"
              name="washerLocation"
              value={formData.washerLocation}
              onChange={handleInputChange}
            >
              <option value="">Select location</option>
              <option value="in-unit">🏠 In-unit (your apartment)</option>
              <option value="shared">🏢 Shared facility (building laundry room)</option>
              <option value="none">❌ None (laundromat required)</option>
            </select>
          </div>

          <div className="form-group">
            <label>Where is the dryer located?</label>
            <select
              id="dryerLocation"
              name="dryerLocation"
              value={formData.dryerLocation}
              onChange={handleInputChange}
            >
              <option value="">Select location</option>
              <option value="in-unit">🏠 In-unit (your apartment)</option>
              <option value="shared">🏢 Shared facility (building laundry room)</option>
              <option value="none">❌ None (air dry or laundromat)</option>
            </select>
          </div>

          <div className="form-group">
            <label>How would you rate the laundry situation overall?</label>
            {renderSentimentScale('laundryQuality')}
            <div className="scale-description">
              😊 Excellent (convenient, well-maintained) → 😤 Poor (broken machines, long waits)
            </div>
          </div>
        </div>

        {/* Amenities & Systems */}
        <div className="form-section">
          <h3>🏗️ Amenities & Systems</h3>
          <div className="form-group">
            <label>Air conditioning availability</label>
            {renderYesNo('acInAllRooms', 'Is AC available in all rooms?')}
            {formData.acInAllRooms === 'no' && renderTextArea('acNotes', 'AC details', 'Which rooms have AC? Any issues with temperature control?')}
          </div>

          <div className="form-group">
            <label>Exhaust fans/ventilation</label>
            {renderYesNo('extractorsInBathrooms', 'Are there exhaust fans in all bathrooms?')}
            {formData.extractorsInBathrooms === 'no' && renderTextArea('extractorNotes', 'Ventilation details', 'Which bathrooms have fans? Any mold/mildew issues?')}
          </div>

          {renderYesNo('petsAllowed', 'Are pets allowed in this rental?')}
          {formData.petsAllowed === 'yes' && renderTextInput('petsExtraCharge', 'Pet deposit or monthly fee', 'text', 'Amount (if any)')}

          {renderYesNo('garageStorage', 'Is garage or additional storage space provided?')}
          {formData.garageStorage === 'yes' && renderTextInput('storageSize', 'Storage size/description', 'text', 'Square footage or description')}

          <div className="form-group">
            <label>How would you rate the overall maintenance quality?</label>
            {renderSentimentScale('maintenanceQuality')}
            <div className="scale-description">
              😊 Excellent (always fixed promptly) → 😤 Poor (constant issues ignored)
            </div>
          </div>

          {renderYesNo('leakyRoofsThefts', 'Have you experienced roof leaks or thefts in the building?')}
          {formData.leakyRoofsThefts === 'yes' && renderTextArea('leakyDetails', 'Leak/theft details', 'Describe the issues and how they were handled')}
        </div>

        {/* Odors & Smells */}
        <div className="form-section">
          <h3>Odors & Smells</h3>
          {renderTextArea('externalOdors', 'External odors', 'Smells from outside...')}
          {renderTextArea('internalOdors', 'Internal odors', 'Smells inside the property...')}
          {renderYesNo('basementSmells', 'Basement smells infiltrating?')}
          {renderYesNo('sharedSmells', 'Shared smells from other apartments?')}
        </div>

        {/* Utilities */}
        <div className="form-section">
          <h3>Utilities</h3>
          {renderTextInput('electricityBill', 'Electricity bill size')}
          {renderYesNo('electricityIncluded', 'Electricity included?')}
          {renderTextInput('waterBill', 'Water bill size')}
          {renderYesNo('waterIncluded', 'Water included?')}
          {renderTextInput('gasBill', 'Gas bill size')}
          {renderYesNo('gasIncluded', 'Gas included?')}
          {renderTextInput('internetBill', 'Internet bill size')}
          {renderYesNo('internetIncluded', 'Internet included?')}
        </div>

        {/* Landlord & Management */}
        <div className="form-section">
          <h3>👨‍💼 Landlord & Property Management</h3>
          <div className="form-group">
            <label>How responsive is the landlord/property manager to maintenance requests?</label>
            {renderSentimentScale('landlordResponsiveness')}
            <div className="scale-description">
              😊 Excellent (fixes issues within hours/days) → 😤 Poor (takes weeks/ignores requests)
            </div>
          </div>

          <div className="form-group">
            <label>How professional is the landlord's communication and behavior?</label>
            {renderSentimentScale('landlordProfessionalism')}
            <div className="scale-description">
              😊 Very professional (courteous, reliable) → 😤 Unprofessional (rude, unreliable)
            </div>
          </div>

          <div className="form-group">
            <label>Overall feeling about landlord ethics and trustworthiness</label>
            {renderEmojiSelector('landlordEthicsEmoji', 'How would you rate their integrity?')}
            {renderTextArea('landlordEthics', 'Ethics details', 'Any concerns about honesty, fairness, or business practices?')}
          </div>

          <div className="form-group">
            <label>Does the landlord seem careless or neglectful about property maintenance?</label>
            {renderSentimentScale('landlordCarelessness')}
            <div className="scale-description">
              😊 Very attentive (proactive maintenance) → 😤 Extremely careless (dangerous neglect)
            </div>
          </div>
          {formData.landlordCarelessness >= 7 && renderTextArea('landlordCarelessnessDetails', 'Carelessness examples', 'Specific examples of neglect or poor maintenance practices')}
        </div>

        {/* Other Issues & Details */}
        <div className="form-section">
          <h3>📝 Other Issues & Details</h3>
          {renderTextArea('otherProblems', 'Any other problems or concerns?', 'List any additional issues you encountered...')}
          {renderTextArea('otherDetails', 'Any other details or experiences to share?', 'Share anything else that might help other renters...')}
        </div>

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="cancel-btn">
            Cancel
          </button>
          <button type="submit" disabled={isLoading} className="submit-btn">
            {isLoading ? 'Submitting...' : 'Submit Review'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReviewForm;
